import React, { useEffect } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Box } from '@mui/material'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import CustomBox from 'components/Form/CustomBox'
import { Roles, speakerStatus, subjectReason, SubjectStatus, SubjectType } from 'constants/index'
import { useDispatch, useSelector } from 'react-redux'
import subjectSlice, { fetchSubjectById, resetSubject, submitSubjectData } from 'store/subject'
import { fetchConferenceById } from 'store/conference'
import 'views/admin/subjects/styles.css'
import Speaker from 'views/admin/subjects/HTMLForms/Speaker'
import moment from 'moment'
import { addSpeaker, getExistingSpeakerWithEmail, updateSpeakerById } from 'store/speaker'
import { toast } from 'react-toastify'
import Sponsor from 'views/admin/subjects/HTMLForms/Sponsor'
import { addSponsor, getExistingSponsorWithEmail, updateSponsorById } from 'store/sponsor'
import Exhibitor from 'views/admin/subjects/HTMLForms/Exhibitor'
import { addVendor, getExistingExhibitorWithEmail, updateVendorById } from 'store/vendor'
import { getSponsorshipLevels } from 'store/sponsorship-level'
import BulkImportSubjectsForm from 'views/admin/subjects/FormikForms/BulkImportSubjectsForm'
import UpdateSubjectForm from 'views/admin/subjects/FormikForms/UpdateSubjectForm'
import { updateTalksWithSpeaker } from 'store/talk'
import { copySubjectImageToSpecificPath } from 'utils/subject'

const SubjectForm = () => {
  const dispatch = useDispatch()
  const { subjectId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const conferenceId = new URLSearchParams(location.search).get('conferenceId')

  const { user } = useSelector((state) => state.auth)
  const { inProgress, subject } = useSelector((state) => state.subjects)
  const { sponsorshipLevels } = useSelector((state) => state.sponsorshipLevel)

  useEffect(() => {
    if (!subjectId) {
      dispatch(subjectSlice.actions.setProgress(false))
    }
  }, [])

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && conferenceId) {
      dispatch(fetchConferenceById(conferenceId))
    } else {
      dispatch(fetchConferenceById(user.conferenceId))
    }
    if (subjectId) {
      dispatch(fetchSubjectById(subjectId))
      dispatch(fetchConferenceById(subject?.conferenceId))
    }
  }, [dispatch, subjectId, subject?.conferenceId])

  useEffect(() => {
    if (subject?.conferenceId && subject?.type === SubjectType.SPONSOR) {
      dispatch(getSponsorshipLevels(subject?.conferenceId))
    }
  }, [dispatch, subject?.conferenceId])

  const goBack = () => {
    dispatch(resetSubject())
    history.goBack()
  }

  const handleSubmitSpeakerForm = async (values, reason) => {
    try {
      const nowDateTime = moment.utc()
      const payload = {
        email: values?.email.trim(),
        fullName: values?.fullName.trim(),
        bio: values?.bio.trim(),
        company: values?.company.trim(),
        position: values?.position.trim(),
        websiteUrl: values?.websiteUrl.trim(),
        status: SubjectStatus.APPROVED,
        approvedAt: nowDateTime.valueOf()
      }
      payload.imageName = subject?.imageName ? subject?.imageName : null

      const updatedPayload = { ...payload }
      delete updatedPayload['status']
      delete updatedPayload['approvedAt']

      if (reason === subjectReason.afterApproval) {
        delete payload['approvedAt']
      }

      const existingSpeaker = await getExistingSpeakerWithEmail(
        values?.email.trim(),
        subject?.conferenceId
      )

      let speakerId
      if (subject?.speakerId || reason === subjectReason.afterApproval) {
        const newSpeakerPayload = { ...updatedPayload, subjectId: subject?.id }
        if (existingSpeaker?.isSubject && existingSpeaker?.status === speakerStatus.PENDING) {
          newSpeakerPayload.status = speakerStatus.APPROVED
        }
        const res = await dispatch(
          updateSpeakerById(subject?.speakerId, newSpeakerPayload, subject?.conferenceId)
        )
        speakerId = res ? subject?.speakerId : null
      } else if (existingSpeaker?.id) {
        const newSpeakerPayload = { ...updatedPayload, subjectId: subject?.id }
        if (existingSpeaker?.isSubject && existingSpeaker?.status === speakerStatus.PENDING) {
          newSpeakerPayload.status = speakerStatus.APPROVED
        }
        const res = await dispatch(
          updateSpeakerById(existingSpeaker?.id, newSpeakerPayload, subject?.conferenceId)
        )
        speakerId = res ? existingSpeaker?.id : null
      } else {
        speakerId = await dispatch(
          addSpeaker(
            {
              ...updatedPayload,
              subjectId: subject?.id
            },
            subject?.conferenceId
          )
        )
      }

      subject?.imageName &&
        reason !== subjectReason.registration &&
        (await copySubjectImageToSpecificPath(
          subject.imageName,
          'subjects',
          'speakers',
          subject?.conferenceId
        ))

      speakerId && (await dispatch(submitSubjectData(subject?.id, { ...payload, speakerId })))
      speakerId && (await updateTalksWithSpeaker(subject?.id, speakerId))
      dispatch(subjectSlice.actions.setSubject({ ...subject, ...payload }))
      toast.success(
        reason === subjectReason.forApproval
          ? 'Subject approved successfully'
          : `${subject?.type} updated successfully`
      )
      goBack()
      return speakerId
    } catch (err) {
      // do nothing
    }
  }

  const handleSubmitSponsorForm = async (values, reason) => {
    try {
      const nowDateTime = moment.utc()
      const payload = {
        email: values.email.trim(),
        fullName: values.fullName.trim(),
        intro: values.intro.trim(),
        websiteUrl: values.websiteUrl.trim(),
        sponsorMessage: values.sponsorMessage.trim(),
        sponsorshipLevelId: values.sponsorshipLevelId.trim(),
        sponsorAdTargetURL: values.sponsorAdTargetURL.trim(),
        status: SubjectStatus.APPROVED,
        booth: values?.booth ? values?.booth.trim() : '',
        approvedAt: nowDateTime.valueOf()
      }
      payload.adImageName = subject?.adImageName ? subject?.adImageName : null
      payload.logoImageName = subject?.logoImageName ? subject?.logoImageName : null
      payload.brochureImageName = subject?.brochureImageName ? subject?.brochureImageName : null

      const updatedPayload = { ...payload }

      delete updatedPayload['status']
      delete updatedPayload['approvedAt']

      if (reason === subjectReason.afterApproval) {
        delete payload['approvedAt']
      }

      const existingSponsor = await getExistingSponsorWithEmail(
        values?.email.trim(),
        subject?.conferenceId
      )

      let sponsorId
      if (subject?.sponsorId || reason === subjectReason.afterApproval) {
        const res = await dispatch(
          updateSponsorById(subject?.sponsorId, updatedPayload, subject?.conferenceId)
        )
        sponsorId = res ? subject?.sponsorId : null
      } else if (existingSponsor?.id) {
        const res = await dispatch(
          updateSponsorById(
            existingSponsor?.id,
            { ...updatedPayload, subjectId: subject?.id },
            subject?.conferenceId
          )
        )
        sponsorId = res ? existingSponsor?.id : null
      } else {
        sponsorId = await dispatch(
          addSponsor({ ...updatedPayload, subjectId: subject?.id }, subject?.conferenceId)
        )
      }

      if (reason !== subjectReason.registration) {
        await Promise.all([
          subject?.adImageName &&
            copySubjectImageToSpecificPath(
              subject.adImageName,
              'subjects',
              'sponsors',
              subject?.conferenceId
            ),
          subject?.logoImageName &&
            copySubjectImageToSpecificPath(
              subject.logoImageName,
              'subjects',
              'sponsors',
              subject?.conferenceId
            ),
          subject?.brochureImageName &&
            copySubjectImageToSpecificPath(
              subject.brochureImageName,
              'subjects',
              'sponsors',
              subject?.conferenceId
            )
        ])
      }
      sponsorId && (await dispatch(submitSubjectData(subject?.id, { ...payload, sponsorId })))
      await dispatch(subjectSlice.actions.setSubject({ ...subject, ...payload }))
      toast.success(
        reason === subjectReason.forApproval
          ? 'Subject approved successfully'
          : `${subject?.type} updated successfully`
      )
      goBack()
    } catch (err) {
      // do nothing
    }
  }

  const handleSubmitExhibitorForm = async (values, reason) => {
    try {
      const nowDateTime = moment.utc()
      const payload = {
        email: values.email.trim(),
        name: values.name.trim(),
        booth: values.booth.trim(),
        websiteUrl: values.websiteUrl.trim(),
        intro: values.intro.trim(),
        message: values.message.trim(),
        status: SubjectStatus.APPROVED,
        approvedAt: nowDateTime.valueOf()
      }
      payload.logoImage = subject?.logoImage ? subject?.logoImage : null

      const updatedPayload = { ...payload }

      delete updatedPayload['status']
      delete updatedPayload['approvedAt']

      if (reason === subjectReason.afterApproval) {
        delete payload['approvedAt']
      }

      const existingExhibitor = await getExistingExhibitorWithEmail(
        values?.email.trim(),
        subject?.conferenceId
      )

      let vendorId
      if (subject?.vendorId || reason === subjectReason.afterApproval) {
        const res = await dispatch(
          updateVendorById(subject?.vendorId, updatedPayload, subject?.conferenceId)
        )
        vendorId = res ? subject?.vendorId : null
      } else if (existingExhibitor?.id) {
        const res = await dispatch(
          updateVendorById(
            existingExhibitor?.id,
            { ...updatedPayload, subjectId: subject?.id },
            subject?.conferenceId
          )
        )
        vendorId = res ? existingExhibitor?.id : null
      } else {
        vendorId = await dispatch(
          addVendor({ ...updatedPayload, subjectId: subject?.id }, subject?.conferenceId)
        )
      }
      if (subject?.logoImage && reason !== subjectReason.registration) {
        await copySubjectImageToSpecificPath(
          subject.logoImage,
          'subjects',
          'vendors',
          subject?.conferenceId
        )
      }

      vendorId && (await dispatch(submitSubjectData(subject?.id, { ...payload, vendorId })))
      await dispatch(subjectSlice.actions.setSubject({ ...subject, ...payload }))
      toast.success(
        reason === subjectReason.forApproval
          ? 'Subject approved successfully'
          : `${subject?.type} updated successfully`
      )
      dispatch(resetSubject())
      goBack()
    } catch (err) {
      console.error(err)
      // do nothing
    }
  }

  if (inProgress)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )

  return (
    <CustomBox>
      {!subjectId && (
        <BulkImportSubjectsForm
          conferenceId={user?.role === Roles.SUPER_ADMIN ? conferenceId : user?.conferenceId}
        />
      )}

      {subjectId &&
      (subject?.status === SubjectStatus.INVITE_NOT_SENT ||
        subject?.status === SubjectStatus.AWAITING_RESPONSE) ? (
        <UpdateSubjectForm subjectId={subjectId} />
      ) : (
        <>
          {subjectId && subject?.type === SubjectType.SPEAKER && (
            <Speaker
              subject={subject}
              handleSubmit={handleSubmitSpeakerForm}
              submitTitle={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW ? 'Save & Approve' : 'Save'
              }
              reason={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW
                  ? subjectReason.forApproval
                  : subjectReason.afterApproval
              }
            />
          )}

          {subjectId && subject?.type === SubjectType.SPONSOR && (
            <Sponsor
              subject={subject}
              showSponsorshipLevel={true}
              sponsorshipLevels={sponsorshipLevels}
              handleSubmit={handleSubmitSponsorForm}
              submitTitle={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW ? 'Save & Approve' : 'Save'
              }
              reason={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW
                  ? subjectReason.forApproval
                  : subjectReason.afterApproval
              }
            />
          )}

          {subjectId && subject?.type === SubjectType.EXHIBITOR && (
            <Exhibitor
              subject={subject}
              handleSubmit={handleSubmitExhibitorForm}
              submitTitle={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW ? 'Save & Approve' : 'Save'
              }
              reason={
                subject?.status === SubjectStatus.PENDING_DATA_REVIEW
                  ? subjectReason.forApproval
                  : subjectReason.afterApproval
              }
            />
          )}
        </>
      )}
    </CustomBox>
  )
}

export default SubjectForm
